import { PropRuntimeGraph } from '../../../../../shared/types/PropRuntimeGraph';
import { RuntimePropInstance } from '../PropMechanicsTypes';
import { RuntimeNodeBase } from './RuntimeNodeBase';
import { uniqueLiveItems } from './RuntimeNodeUtils';

/**
 * 自身目标节点。
 *
 * 输出当前上下文中的单个目标；在 each 分支内优先输出当前目标，否则输出道具拥有者。
 */
export class TargetSelfNode extends RuntimeNodeBase {
    /**
     * 输出目标端口。
     *
     * @returns 包含一个运行时实例的目标数组。
     */
    public target(): RuntimePropInstance[] {
        return [this.context.currentTarget ?? this.context.owner];
    }
}

/**
 * 盘面原料目标节点。
 *
 * 根据 `scope`、`position` 和 `includeEmpty` 从盘面选择目标。
 * 默认不包含数据库中的“空位置”实例；勾选 includeEmpty 后才会把空位置作为目标输出。
 */
export class TargetBoardNode extends RuntimeNodeBase {
    /**
     * 输出目标数组端口。
     *
     * @returns 符合盘面范围规则的真实道具实例。
     */
    public targets(): RuntimePropInstance[] {
        const scope = String(this.node.data?.scope ?? 'all');
        if (scope === 'relative') return this.selectRelativeTargets();
        if (scope === 'absolute') return this.selectAbsoluteTargets();
        return this.context.getAllBoardItems(this.includeEmpty());
    }

    private selectRelativeTargets(): RuntimePropInstance[] {
        const base = this.context.currentTarget ?? this.context.owner;
        if (!base || base.row < 0 || base.col < 0) return [];
        const positions = normalizePositions(this.node.data?.position, ['adjacent']);
        const includeEmpty = this.includeEmpty();
        const candidates = this.context.getAllBoardItems(includeEmpty).filter((item) => item.id !== base.id);
        return uniqueLiveItems(candidates.filter((item) => isRelativeMatched(item, base, positions)), includeEmpty);
    }

    private selectAbsoluteTargets(): RuntimePropInstance[] {
        const positions = normalizePositions(this.node.data?.position, ['corner']);
        const includeEmpty = this.includeEmpty();
        const candidates = this.context.getAllBoardItems(includeEmpty);
        return uniqueLiveItems(candidates.filter((item) => isAbsoluteMatched(
            item,
            this.context.state.rows,
            this.context.state.cols,
            this.context.state.firstRowIndex,
            this.context.state.firstColumnIndex,
            positions,
        )), includeEmpty);
    }

    private includeEmpty(): boolean {
        return this.node.data?.includeEmpty === true;
    }
}

/**
 * 原型库节点。
 *
 * 输出当前配置目录中的道具原型，可按道具类型筛选；衍生物默认不进入原型库候选。
 */
export class PrototypeLibraryNode extends RuntimeNodeBase {
    /**
     * 输出原型数组端口。
     *
     * @returns 当前原型库候选列表。
     */
    public prototypes(): PropRuntimeGraph[] {
        const propType = Number(this.node.data?.propType ?? 0);
        const items = propType > 0
            ? this.context.state.catalog.filter((item) => item.type === propType)
            : this.context.state.catalog;
        return items.filter((item) => !item.derived);
    }
}

/**
 * 原型选择节点。
 *
 * 通过 `prototypeId` 输出指定道具原型，用于生成、转化和原型筛选。
 */
export class PrototypeSelectNode extends RuntimeNodeBase {
    /**
     * 输出原型数组端口。
     *
     * @returns 找到时返回单个原型；找不到时返回空数组。
     */
    public prototypes(): PropRuntimeGraph[] {
        const prototypeId = String(this.node.data?.prototypeId ?? '');
        if (!prototypeId) return [];
        const graph = this.context.state.catalog.find((item) => item._id === prototypeId);
        if (graph) return [graph];
        this.context.warn(`原型选择节点 ${this.node.id} 未在当前 catalog 中找到原型 ${prototypeId}`);
        return [];
    }
}

function normalizePositions(rawPosition: unknown, fallback: string[]): string[] {
    const raw = Array.isArray(rawPosition) ? rawPosition : rawPosition ? [rawPosition] : fallback;
    const result = raw.map((item) => String(item)).filter(Boolean);
    return result.length ? result : fallback;
}

function isRelativeMatched(item: RuntimePropInstance, base: RuntimePropInstance, positions: string[]): boolean {
    const rowDelta = Math.abs(item.row - base.row);
    const colDelta = Math.abs(item.col - base.col);
    return positions.some((position) => {
        if (position === 'adjacent') return rowDelta + colDelta === 1;
        if (position === 'sameRow') return item.row === base.row;
        if (position === 'sameColumn') return item.col === base.col;
        if (position === 'diagonal') return rowDelta === colDelta && rowDelta > 0;
        return false;
    });
}

function isAbsoluteMatched(item: RuntimePropInstance, rows: number, cols: number, firstRow: number, firstCol: number, positions: string[]): boolean {
    const lastRow = firstRow + rows - 1;
    const lastCol = firstCol + cols - 1;
    const middleCol = firstCol + Math.floor(cols / 2);
    return positions.some((position) => {
        if (position === 'corner') return (item.row === firstRow || item.row === lastRow) && (item.col === firstCol || item.col === lastCol);
        if (position === 'edge') return item.row === firstRow || item.row === lastRow || item.col === firstCol || item.col === lastCol;
        if (position === 'firstRow') return item.row === firstRow;
        if (position === 'lastRow') return item.row === lastRow;
        if (position === 'firstColumn') return item.col === firstCol;
        if (position === 'lastColumn') return item.col === lastCol;
        if (position === 'middleColumn') return item.col === middleCol;
        return false;
    });
}
